import React, { useState, useEffect, useCallback } from 'react';
import { useApiAuth } from '../../../contexts/ApiAuthContext';
import { calendarService } from '..';
import { roleService } from '../../users';
import { toast } from 'react-hot-toast';
import {
  Calendar, Plus, X, ChevronLeft, ChevronRight,
  Loader2, HardDrive, Trash2, Eye,
} from 'lucide-react';
import {
  format, startOfMonth, endOfMonth, eachDayOfInterval,
  isSameMonth, isToday, addMonths, subMonths,
  startOfWeek, endOfWeek,
} from 'date-fns';
import { BRAND_COLORS, SYSTEM_COLORS, NEUTRAL_COLORS } from '../../../constants/brandPalette';

const LOCAL_KEY = 'calendarEvents';

const readLocal = () => {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
};

const writeLocal = (events) => localStorage.setItem(LOCAL_KEY, JSON.stringify(events));

const inp = `w-full px-3 py-2 rounded-xl border border-gray-200 bg-white text-sm outline-none
  transition-all focus:ring-2 focus:ring-[#37559e]/20 focus:border-[#37559e] hover:border-gray-300`;

const EVENT_COLORS = [
  BRAND_COLORS.primary, BRAND_COLORS.accent,
  '#10b981', '#8b5cf6', '#f59e0b', '#ec4899',
];

const colorFor = (str = '') => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return EVENT_COLORS[Math.abs(h) % EVENT_COLORS.length];
};

const CalendarPage = () => {
  const { user } = useApiAuth();

  const [events, setEvents] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLocalMode, setIsLocalMode] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showCreate, setShowCreate] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    title: '', description: '',
    startDate: '', endDate: '',
    visibility: 'everyone', visibleToRoles: [],
  });

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);

    try {
      const [eventsData, rolesData] = await Promise.allSettled([
        calendarService.getEvents(),
        roleService.getRoles(),
      ]);

      if (eventsData.status === 'fulfilled') {
        setEvents(eventsData.value);
      } else {
        setEvents(readLocal());
        setIsLocalMode(true);
      }

      if (rolesData.status === 'fulfilled') {
        const rd = rolesData.value;
        setRoles(Array.isArray(rd) ? rd : (rd?.roles || rd?.data || []));
      }
    } catch {
      setEvents(readLocal());
      setIsLocalMode(true);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => setForm({
    title: '', description: '', startDate: '', endDate: '',
    visibility: 'everyone', visibleToRoles: [],
  });

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.startDate || !form.endDate) {
      toast.error('Title, start date and end date are required');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, title: form.title.trim(), createdBy: user.id, createdAt: new Date().toISOString() };
      const created = await calendarService.createEvent(payload);
      setEvents(prev => [...prev, created]);
      toast.success('Event created');
      setShowCreate(false);
      resetForm();
    } catch {
      toast.error('Failed to create event');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await calendarService.deleteEvent(id);
      setEvents(prev => prev.filter(e => e.id !== id));
      setSelectedEvent(null);
      toast.success('Event deleted');
    } catch {
      toast.error('Failed to delete event');
    }
  };

  const calDays = eachDayOfInterval({
    start: startOfWeek(startOfMonth(currentDate), { weekStartsOn: 0 }),
    end: endOfWeek(endOfMonth(currentDate), { weekStartsOn: 0 }),
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 gap-3">
        <Loader2 size={20} className="animate-spin" style={{ color: BRAND_COLORS.primary }} />
        <span className="text-sm text-gray-500">Loading calendar…</span>
      </div>
    );
  }

  return (
    <div className="space-y-5">

      {/* Local storage notice */}
      {isLocalMode && (
        <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl text-sm border"
          style={{
            backgroundColor: '#fffbeb',
            borderColor: '#fde68a',
            color: '#92400e',
          }}>
          <HardDrive size={14} className="flex-shrink-0" />
          <span>
            <strong>Offline mode</strong> — events API is not yet available. Events are saved locally in your browser.
          </span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold" style={{ color: NEUTRAL_COLORS.black }}>Calendar</h1>
          <p className="text-sm text-gray-500 mt-0.5">Team events and schedules</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-opacity hover:opacity-90"
          style={{ backgroundColor: BRAND_COLORS.primary }}
        >
          <Plus size={15} />
          Add Event
        </button>
      </div>

      {/* Calendar card */}
      <div className="rounded-2xl border overflow-hidden"
        style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>

        {/* Month nav */}
        <div className="flex items-center justify-between px-5 py-4 border-b"
          style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
          <h2 className="text-base font-semibold" style={{ color: NEUTRAL_COLORS.black }}>
            {format(currentDate, 'MMMM yyyy')}
          </h2>
          <div className="flex items-center gap-1">
            <button onClick={() => setCurrentDate(subMonths(currentDate, 1))}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors">
              <ChevronLeft size={16} className="text-gray-500" />
            </button>
            <button onClick={() => setCurrentDate(new Date())}
              className="px-3 py-1 text-xs font-semibold rounded-lg hover:bg-gray-100 transition-colors"
              style={{ color: BRAND_COLORS.primary }}>
              Today
            </button>
            <button onClick={() => setCurrentDate(addMonths(currentDate, 1))}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors">
              <ChevronRight size={16} className="text-gray-500" />
            </button>
          </div>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 border-b" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
            <div key={d} className="py-2.5 text-center text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
              {d}
            </div>
          ))}
        </div>

        {/* Day cells */}
        <div className="grid grid-cols-7">
          {calDays.map((date, i) => {
            const dayEvents = events.filter(ev => {
              try { return new Date(ev.startDate).toDateString() === date.toDateString(); }
              catch { return false; }
            });
            const outside = !isSameMonth(date, currentDate);
            const today = isToday(date);

            return (
              <div key={i}
                className={`min-h-[90px] p-2 border-b border-r ${
                  i % 7 === 6 ? 'border-r-0' : ''
                } ${i >= calDays.length - 7 ? 'border-b-0' : ''}`}
                style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: outside ? SYSTEM_COLORS.shellSurfaceMuted : 'transparent' }}
              >
                <div className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-medium mb-1.5 ${
                  today ? 'text-white' : outside ? 'text-gray-400' : 'text-gray-700'
                }`}
                  style={today ? { backgroundColor: BRAND_COLORS.primary } : {}}
                >
                  {format(date, 'd')}
                </div>
                {dayEvents.slice(0, 3).map(ev => (
                  <button key={ev.id}
                    onClick={() => setSelectedEvent(ev)}
                    className="w-full text-left text-[10px] font-medium px-1.5 py-0.5 rounded-md mb-0.5 truncate text-white transition-opacity hover:opacity-80"
                    style={{ backgroundColor: colorFor(ev.title) }}
                  >
                    {ev.title}
                  </button>
                ))}
                {dayEvents.length > 3 && (
                  <p className="text-[10px] text-gray-400 px-1">+{dayEvents.length - 3} more</p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Create Event Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="w-full max-w-md flex flex-col max-h-[90vh] rounded-2xl shadow-2xl overflow-hidden"
            style={{ backgroundColor: SYSTEM_COLORS.shellSurface }}>

            <div className="flex items-center justify-between flex-shrink-0 px-5 py-4"
              style={{ backgroundColor: BRAND_COLORS.primary }}>
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center">
                  <Calendar size={14} className="text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">New Event</p>
                  <p className="text-[10px] text-white/70">Add to team calendar</p>
                </div>
              </div>
              <button onClick={() => { setShowCreate(false); resetForm(); }}
                className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20">
                <X size={14} className="text-white" />
              </button>
            </div>

            <form onSubmit={handleCreate} className="flex-1 overflow-y-auto p-5 space-y-4">
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">
                  Title <span className="text-red-400">*</span>
                </label>
                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="Event title" className={inp} required />
              </div>

              <div>
                <label className="block text-[10px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Description</label>
                <textarea value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Optional description" className={inp} rows={2} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">
                    Start <span className="text-red-400">*</span>
                  </label>
                  <input type="datetime-local" value={form.startDate}
                    onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
                    className={inp} required />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">
                    End <span className="text-red-400">*</span>
                  </label>
                  <input type="datetime-local" value={form.endDate}
                    onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))}
                    className={inp} required />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Visibility</label>
                <select value={form.visibility}
                  onChange={e => setForm(f => ({ ...f, visibility: e.target.value, visibleToRoles: [] }))}
                  className={inp}>
                  <option value="everyone">Everyone</option>
                  <option value="selected">Selected Roles</option>
                </select>
              </div>

              {form.visibility === 'selected' && roles.length > 0 && (
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 mb-2 uppercase tracking-wide">Roles</label>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-2 max-h-36 overflow-y-auto rounded-xl border p-3"
                    style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
                    {roles.map(role => (
                      <label key={role.id || role.key || role.name}
                        className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer select-none">
                        <input type="checkbox"
                          checked={form.visibleToRoles.includes(role.id || role.key)}
                          onChange={e => {
                            const val = role.id || role.key;
                            setForm(f => ({
                              ...f,
                              visibleToRoles: e.target.checked
                                ? [...f.visibleToRoles, val]
                                : f.visibleToRoles.filter(v => v !== val),
                            }));
                          }}
                          className="rounded"
                          style={{ accentColor: BRAND_COLORS.primary }}
                        />
                        {role.name || role.key || role}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-1">
                <button type="button"
                  onClick={() => { setShowCreate(false); resetForm(); }}
                  className="px-4 py-2 text-sm rounded-xl border hover:bg-gray-50 transition-colors"
                  style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
                  Cancel
                </button>
                <button type="submit" disabled={saving}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-xl transition-opacity hover:opacity-90 disabled:opacity-60"
                  style={{ backgroundColor: BRAND_COLORS.primary }}>
                  {saving && <Loader2 size={13} className="animate-spin" />}
                  Create Event
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Event Detail Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="w-full max-w-sm flex flex-col rounded-2xl shadow-2xl overflow-hidden"
            style={{ backgroundColor: SYSTEM_COLORS.shellSurface }}>

            <div className="flex items-center justify-between flex-shrink-0 px-5 py-4"
              style={{ backgroundColor: colorFor(selectedEvent.title) }}>
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center">
                  <Eye size={14} className="text-white" />
                </div>
                <p className="text-sm font-semibold text-white truncate max-w-[180px]">{selectedEvent.title}</p>
              </div>
              <button onClick={() => setSelectedEvent(null)}
                className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20">
                <X size={14} className="text-white" />
              </button>
            </div>

            <div className="p-5 space-y-3">
              {selectedEvent.description && (
                <div>
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Description</p>
                  <p className="text-sm text-gray-700">{selectedEvent.description}</p>
                </div>
              )}
              <div>
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Time</p>
                <p className="text-sm text-gray-700">
                  {selectedEvent.startDate ? format(new Date(selectedEvent.startDate), 'PPp') : '—'}
                  {selectedEvent.endDate ? ` → ${format(new Date(selectedEvent.endDate), 'PPp')}` : ''}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Visibility</p>
                <span className="text-xs px-2 py-0.5 rounded-full font-medium capitalize"
                  style={{ backgroundColor: SYSTEM_COLORS.primaryTint, color: BRAND_COLORS.primary }}>
                  {selectedEvent.visibility || 'everyone'}
                </span>
              </div>
            </div>

            <div className="flex justify-between items-center px-5 py-3 border-t"
              style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
              <button onClick={() => handleDelete(selectedEvent.id)}
                className="flex items-center gap-1.5 text-xs font-semibold text-red-600 hover:text-red-700 transition-colors">
                <Trash2 size={13} />
                Delete
              </button>
              <button onClick={() => setSelectedEvent(null)}
                className="px-4 py-1.5 text-sm rounded-xl hover:bg-gray-100 transition-colors text-gray-600">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarPage;
